import {
  findOutboundMediaCapability,
  isNormalizableRecorderMime,
  platformAcceptsNormalizedRecordedVoice,
  type OutboundMediaCapabilitiesResponse,
} from "@multi-chat/shared";

const RECORDER_MIME_CANDIDATES = [
  "audio/mp4",
  "audio/mp4;codecs=mp4a",
  "audio/aac",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
] as const;

export type OutboundVoiceRecorderCapability = {
  supported: boolean;
  mimeType: string | null;
  maxBytes: number;
  reasonKey?: string;
};

function normalizeMimeBase(mimeType: string): string {
  return mimeType.toLowerCase().split(";")[0]?.trim() ?? "";
}

export function resolveOutboundVoiceRecorderCapability(
  capabilities: OutboundMediaCapabilitiesResponse | null,
): OutboundVoiceRecorderCapability {
  if (!capabilities) {
    return {
      supported: false,
      mimeType: null,
      maxBytes: 0,
      reasonKey: "inbox.composer.voiceRecorder.unsupportedPlatform",
    };
  }

  const audioCapability = findOutboundMediaCapability(capabilities.platform, "AUDIO");
  if (!audioCapability) {
    return {
      supported: false,
      mimeType: null,
      maxBytes: 0,
      reasonKey: "inbox.composer.voiceRecorder.unsupportedPlatform",
    };
  }

  if (!platformAcceptsNormalizedRecordedVoice(audioCapability.allowedMimeTypes)) {
    return {
      supported: false,
      mimeType: null,
      maxBytes: audioCapability.maxBytes,
      reasonKey: "inbox.composer.voiceRecorder.unsupportedPlatform",
    };
  }

  if (
    typeof MediaRecorder === "undefined" ||
    typeof navigator?.mediaDevices?.getUserMedia !== "function"
  ) {
    return {
      supported: false,
      mimeType: null,
      maxBytes: audioCapability.maxBytes,
      reasonKey: "inbox.composer.voiceRecorder.unsupportedBrowser",
    };
  }

  for (const candidate of RECORDER_MIME_CANDIDATES) {
    if (!MediaRecorder.isTypeSupported(candidate)) {
      continue;
    }
    if (!isNormalizableRecorderMime(candidate)) {
      continue;
    }
    return {
      supported: true,
      mimeType: candidate,
      maxBytes: audioCapability.maxBytes,
    };
  }

  return {
    supported: false,
    mimeType: null,
    maxBytes: audioCapability.maxBytes,
    reasonKey: "inbox.composer.voiceRecorder.unsupportedFormat",
  };
}

export function recordedVoiceFilename(mimeType: string): string {
  const base = normalizeMimeBase(mimeType);
  switch (base) {
    case "audio/mp4":
      return "voice-note.m4a";
    case "audio/aac":
      return "voice-note.aac";
    case "audio/mpeg":
      return "voice-note.mp3";
    case "audio/ogg":
      return "voice-note.ogg";
    case "audio/webm":
      return "voice-note.webm";
    case "audio/wav":
      return "voice-note.wav";
    default:
      return "voice-note.bin";
  }
}
